import { FiMessageCircle, FiSun } from "react-icons/fi";
import { LuCircleGauge, LuGauge, LuPartyPopper } from "react-icons/lu";
import type { IconType } from "react-icons";
import { Text } from "~/components/Text";
import { EventStructure, EventTone } from "~/__generated__/graphql/graphql";

type WhatToExpectPillsProps = {
  tone?: EventTone | null;
  structure?: EventStructure | null;
  variant?: "light" | "dark";
};

const TONE_LABELS: Record<EventTone, { Icon: IconType; label: string }> = {
  [EventTone.LaidBack]: { Icon: LuGauge, label: "Laid back" },
  [EventTone.HighEnergy]: { Icon: LuCircleGauge, label: "High energy" },
  [EventTone.Chatty]: { Icon: FiMessageCircle, label: "Chatty" },
};

const STRUCTURE_LABELS: Record<
  EventStructure,
  { Icon: IconType; label: string }
> = {
  [EventStructure.Casual]: { Icon: FiSun, label: "Casual" },
  [EventStructure.OpenEnded]: { Icon: FiMessageCircle, label: "Open-ended" },
  [EventStructure.Curated]: { Icon: LuPartyPopper, label: "Curated" },
};

export function WhatToExpectPills({
  tone,
  structure,
  variant = "light",
}: WhatToExpectPillsProps) {
  if (!tone && !structure) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {tone && <Pill variant={variant} {...TONE_LABELS[tone]} />}
      {structure && <Pill variant={variant} {...STRUCTURE_LABELS[structure]} />}
    </div>
  );
}

function Pill({
  Icon,
  label,
  variant,
}: {
  Icon: IconType;
  label: string;
  variant: "light" | "dark";
}) {
  const containerClass =
    variant === "dark"
      ? "border-butter-cream-light/40 bg-butter-cream-light/10"
      : "border-butter-green/80 bg-butter-cream-light";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 leading-none ${containerClass}`}
    >
      <Icon
        size={16}
        className={variant === "dark" ? "text-butter-cream-light" : "text-butter-green"}
        aria-hidden
      />
      <Text as="span" variant="secondary" size="sm" color={variant === "dark" ? "light" : "green"}>
        {label}
      </Text>
    </span>
  );
}
